import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Save, Plus, Trash2, Mic, Upload, FolderOpen,
  CircleCheck, CircleSlash, Coffee, Plane, Moon, AlertCircle, UserCog,
  ChevronUp, ChevronDown,
} from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from '../components/Toast';
import AudioPicker from '../components/AudioPicker';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

// Default profile palette — Available / Away / DND / Boss-Secretary.
const DEFAULT_PROFILES: { name: string; kind: 'available' | 'away' | 'dnd' | 'custom'; icon: any }[] = [
  { name: 'Available',      kind: 'available', icon: CircleCheck },
  { name: 'Away',           kind: 'away',      icon: Moon        },
  { name: 'Do Not Disturb', kind: 'dnd',       icon: CircleSlash },
  { name: 'Lunch',          kind: 'custom',    icon: Coffee      },
  { name: 'Business Trip',  kind: 'custom',    icon: Plane       },
  { name: 'Boss-Secretary', kind: 'custom',    icon: UserCog     },
];

const FALLBACK_DEST_TYPES = [
  { v: 'voicemail',    label: 'Send to voicemail' },
  { v: 'extension',    label: 'Forward to extension' },
  { v: 'mobile',       label: 'Forward to mobile' },
  { v: 'external',     label: 'Forward to external number' },
  { v: 'ring_group',   label: 'Forward to ring group' },
  { v: 'queue',        label: 'Forward to queue' },
  { v: 'ivr',          label: 'Forward to IVR' },
  { v: 'end_call',     label: 'End call' },
  { v: 'busy',         label: 'Send busy signal' },
];

type ProfileRow = {
  profile_uuid?: string;
  name: string;
  kind: 'available' | 'away' | 'dnd' | 'custom';
  no_answer_timeout_seconds: number;
  block_push_calls: boolean;
  ring_my_mobile: boolean;
  accept_multiple_calls: boolean;
  disable_ring_group_calls: boolean;
  route_no_answer_internal?: any;
  route_no_answer_external?: any;
  route_busy_internal?: any;
  route_busy_external?: any;
  route_not_registered_internal?: any;
  route_not_registered_external?: any;
  route_all_calls_internal?: any;
  route_all_calls_external?: any;
  greeting_audio_id?: string | null;
  custom_message?: string;
};

// Standalone page (renders the header + delegates the body to ForwardingPanel)
export default function ForwardingProfiles() {
  const { extension_uuid } = useParams<{ extension_uuid: string }>();
  const navigate = useNavigate();
  const userQuery = useQuery<any>({
    queryKey: ['user-detail-fp', extension_uuid],
    queryFn: async () => {
      const r = await fetch(`/api/users`, auth()).then(x => x.json());
      return Array.isArray(r) ? r.find((u: any) => u.extension_uuid === extension_uuid) : null;
    },
    enabled: !!extension_uuid,
  });

  if (!extension_uuid) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/users')} className="btn-ghost inline-flex items-center gap-1"><ArrowLeft size={14} /> Users</button>
        <h1 className="text-xl font-semibold">
          Call Forwarding — {userQuery.data ? `${userQuery.data.name} (ext ${userQuery.data.extension})` : '…'}
        </h1>
      </div>
      <ForwardingPanel extension_uuid={extension_uuid} />
    </div>
  );
}

// Embeddable panel — used inside the User Editor's Call Forwarding tab.
export function ForwardingPanel({ extension_uuid }: { extension_uuid: string }) {
  const qc = useQueryClient();
  const toast = useToast();

  // 'profile:<uuid>' or 'profile:default:<name>' or 'exceptions'
  const [selected, setSelected] = useState<string>('');

  const settings = useQuery<any>({
    queryKey: ['user-settings', extension_uuid],
    queryFn: () => fJson(`/api/users-extended/${extension_uuid}/settings`),
    enabled: !!extension_uuid,
  });
  const profiles = useQuery<any[]>({
    queryKey: ['forwarding-profiles', extension_uuid],
    queryFn: () => fJson(`/api/users-extended/${extension_uuid}/profiles`),
    enabled: !!extension_uuid,
  });
  const exceptions = useQuery<any[]>({
    queryKey: ['forwarding-exceptions', extension_uuid],
    queryFn: () => fJson(`/api/users-extended/${extension_uuid}/exceptions`),
    enabled: !!extension_uuid,
  });

  // Compose the rail: stored profiles + un-saved defaults the user hasn't customized yet.
  const railProfiles = useMemo(() => {
    const stored = profiles.data || [];
    const storedNames = new Set(stored.map(p => p.name));
    const defaults = DEFAULT_PROFILES.filter(d => !storedNames.has(d.name)).map(d => ({
      name: d.name, kind: d.kind, _is_default: true,
    }));
    return [...stored, ...defaults];
  }, [profiles.data]);

  useEffect(() => {
    if (!selected && railProfiles.length) setSelected(`profile:${railProfiles[0].profile_uuid || 'default:' + railProfiles[0].name}`);
  }, [railProfiles, selected]);

  const activeProfileKey = selected.startsWith('profile:') ? selected.slice(8) : '';
  const activeProfile: ProfileRow | undefined = useMemo(() => {
    if (!activeProfileKey) return undefined;
    if (activeProfileKey.startsWith('default:')) {
      const name = activeProfileKey.slice(8);
      const def = DEFAULT_PROFILES.find(d => d.name === name);
      if (!def) return undefined;
      return {
        name: def.name, kind: def.kind,
        no_answer_timeout_seconds: 20,
        block_push_calls: false, ring_my_mobile: false, accept_multiple_calls: true, disable_ring_group_calls: false,
      };
    }
    return profiles.data?.find((p: any) => p.profile_uuid === activeProfileKey);
  }, [activeProfileKey, profiles.data]);

  const isActive = settings.data?.current_profile_name === activeProfile?.name;

  return (
    <div className="flex gap-4 min-h-[600px]">
      {/* Left rail */}
      <div className="w-56 flex-shrink-0 space-y-1">
        {railProfiles.map((p: any) => {
          const def = DEFAULT_PROFILES.find(d => d.name === p.name);
          const Icon = def?.icon || UserCog;
          const key = `profile:${p.profile_uuid || 'default:' + p.name}`;
          const isSel = selected === key;
          const isActiveProfile = settings.data?.current_profile_name === p.name;
          return (
            <button
              key={key}
              onClick={() => setSelected(key)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 ${isSel ? 'bg-primary text-white' : 'hover:bg-elevated'}`}
            >
              <Icon size={14} />
              <span className="flex-1">{p.name}</span>
              {isActiveProfile && <span className="text-xs px-1 rounded bg-success/20 text-success">●</span>}
              {p._is_default && !isSel && <span className="text-xs text-textSecondary">·</span>}
            </button>
          );
        })}
        <button
          onClick={() => setSelected('exceptions')}
          className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 ${selected === 'exceptions' ? 'bg-primary text-white' : 'hover:bg-elevated'}`}
        >
          <AlertCircle size={14} />
          <span className="flex-1">Exceptions</span>
          <span className="text-xs text-textSecondary">{exceptions.data?.length || 0}</span>
        </button>
      </div>

      {/* Right pane */}
      <div className="flex-1 min-w-0">
        {selected === 'exceptions' ? (
          <ExceptionsPanel extension_uuid={extension_uuid!} list={exceptions.data || []} qc={qc} toast={toast} />
        ) : activeProfile?.name === 'Boss-Secretary' ? (
          <BossSecretaryPanel extension_uuid={extension_uuid!} qc={qc} toast={toast} />
        ) : activeProfile ? (
          <ProfilePanel
            key={activeProfileKey}
            extension_uuid={extension_uuid!}
            profile={activeProfile}
            isActiveProfile={!!isActive}
            qc={qc}
            toast={toast}
          />
        ) : null}
      </div>
    </div>
  );
}

function ProfilePanel({ extension_uuid, profile, isActiveProfile, qc, toast }: any) {
  const [form, setForm] = useState<ProfileRow>(profile);
  useEffect(() => setForm(profile), [profile.profile_uuid, profile.name]);

  const save = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/profiles`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['forwarding-profiles', extension_uuid] }); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  const setActive = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/active-profile`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: form.name }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-settings', extension_uuid] }); toast('success', `Active profile: ${form.name}`); },
  });

  const del = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/profiles/${form.profile_uuid}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['forwarding-profiles', extension_uuid] }); toast('success', 'Deleted'); },
  });

  const set = (k: keyof ProfileRow, v: any) => setForm({ ...form, [k]: v });

  // 3CX renders three shapes for the per-status pane:
  //   • "presence" (Available, Lunch)        — full form: Unanswered + Busy + 5 toggles
  //   • "absence"  (Away, DND, Business Trip)— simple: forward int/ext + OoO-VM + 3 toggles
  //   • Lunch and Business Trip ALSO get a "Custom Profile Name" field (renameable)
  const presence = isPresenceProfile(form.name);
  const renameable = form.name === 'Lunch' || form.name === 'Business Trip';

  return (
    <div className="card p-4 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium text-lg">{form.name}</div>
          <div className="text-xs text-textSecondary">{form.kind === 'custom' ? 'Custom profile' : `Built-in (${form.kind})`}</div>
        </div>
        <div className="flex gap-2">
          {!isActiveProfile && <button onClick={() => setActive.mutate()} className="btn-ghost">Make active</button>}
          {form.profile_uuid && form.kind === 'custom' && <button onClick={() => del.mutate()} className="btn-ghost text-danger inline-flex items-center gap-1"><Trash2 size={14} /> Delete</button>}
          <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
        </div>
      </div>

      <div>
        <label className="block text-sm text-textSecondary mb-1">Custom Status Message</label>
        <input className="input" value={form.custom_message || ''} onChange={e => set('custom_message', e.target.value)} placeholder="e.g. In a meeting until 3pm" />
      </div>

      {renameable && (
        <div>
          <label className="block text-sm text-textSecondary mb-1">Custom Profile Name</label>
          <input className="input" value={(form as any).custom_profile_name || ''} onChange={e => set('custom_profile_name' as any, e.target.value)} placeholder={`Override "${form.name}"`} />
        </div>
      )}

      {presence ? (
        <>
          <Section title="Unanswered Calls">
            <div className="space-y-3">
              <Field label="No Answer Timeout">
                <div className="flex items-center gap-2">
                  <input type="number" className="input max-w-[120px]" min={5} max={120}
                    value={form.no_answer_timeout_seconds} onChange={e => set('no_answer_timeout_seconds', parseInt(e.target.value) || 20)} />
                  <span className="text-sm text-textSecondary">in seconds</span>
                </div>
              </Field>
              <DestinationField label="Forward external calls to"
                value={form.route_no_answer_external} onChange={v => set('route_no_answer_external', v)} />
              <DestinationField label="Forward internal calls to" sameAs="all Calls"
                value={form.route_no_answer_internal} onChange={v => set('route_no_answer_internal', v)} />
            </div>
          </Section>

          <Section title="Busy or not Registered">
            <div className="space-y-3">
              <DestinationField label="Forward external calls to"
                value={form.route_busy_external} onChange={(v) => { set('route_busy_external', v); set('route_not_registered_external', v); }} />
              <DestinationField label="Forward internal calls to" sameAs="all Calls"
                value={form.route_busy_internal} onChange={(v) => { set('route_busy_internal', v); set('route_not_registered_internal', v); }} />
            </div>
          </Section>

          <Section title="General options">
            <div className="space-y-1">
              <Toggle label="Also ring my mobile"           v={!!form.ring_my_mobile}             on={v => set('ring_my_mobile', v)} />
              <Toggle label="Accept multiple calls"         v={form.accept_multiple_calls !== false} on={v => set('accept_multiple_calls', v)} />
              <Toggle label="Log out from queues"           v={!!(form as any).log_out_from_queues}  on={v => set('log_out_from_queues' as any, v)} />
              <Toggle label="Accept calls from Ring Groups" v={!form.disable_ring_group_calls}    on={v => set('disable_ring_group_calls', !v)} />
              <Toggle label="Accept PUSH notifications"     v={!form.block_push_calls}            on={v => set('block_push_calls', !v)} />
            </div>
          </Section>
        </>
      ) : (
        <>
          {/* Absence form: forward internal + external with Out-of-Office-hours Voicemail
              checkbox alongside each. No timeout, no Busy section. */}
          <div className="space-y-4">
            <ForwardWithOOH
              label="Forward internal calls to"
              value={form.route_all_calls_internal}
              ooh={(form as any).route_all_internal_ooh_voicemail}
              onValue={(v) => { set('route_all_calls_internal', v); set('route_no_answer_internal', v); set('route_busy_internal', v); }}
              onOOH={(v) => set('route_all_internal_ooh_voicemail' as any, v)}
            />
            <ForwardWithOOH
              label="Forward external calls to"
              value={form.route_all_calls_external}
              ooh={(form as any).route_all_external_ooh_voicemail}
              onValue={(v) => { set('route_all_calls_external', v); set('route_no_answer_external', v); set('route_busy_external', v); }}
              onOOH={(v) => set('route_all_external_ooh_voicemail' as any, v)}
            />
          </div>

          <Section title="General options">
            <div className="space-y-1">
              <Toggle label="Log out from queues"           v={!!(form as any).log_out_from_queues}  on={v => set('log_out_from_queues' as any, v)} />
              <Toggle label="Accept calls from Ring Groups" v={!form.disable_ring_group_calls}    on={v => set('disable_ring_group_calls', !v)} />
              <Toggle label="Accept PUSH notifications"     v={!form.block_push_calls}            on={v => set('block_push_calls', !v)} />
            </div>
          </Section>
        </>
      )}

      <Section title="Greeting">
        <GreetingPicker
          extension_uuid={extension_uuid}
          profile_name={form.name}
          audio_id={form.greeting_audio_id}
          onChange={v => set('greeting_audio_id', v)}
          toast={toast}
        />
      </Section>
    </div>
  );
}

// "Available" + "Lunch" use the presence form; "Away", "Do Not Disturb",
// "Business Trip" (and any custom profile defaulting to absence) use the
// simpler absence form.
function isPresenceProfile(name: string): boolean {
  if (!name) return true;
  const n = name.toLowerCase();
  return n === 'available' || n === 'lunch';
}

// Forward dropdown + paired "Out of Office hours Voicemail" checkbox to its right.
function ForwardWithOOH({ label, value, ooh, onValue, onOOH }: {
  label: string; value: any; ooh: boolean; onValue: (v: any) => void; onOOH: (v: boolean) => void;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
      <DestinationField label={label} value={value} onChange={onValue} />
      <label className="text-sm flex items-center gap-2 pb-1.5 whitespace-nowrap">
        <input type="checkbox" checked={!!ooh} onChange={e => onOOH(e.target.checked)} />
        Out of Office hours Voicemail
      </label>
    </div>
  );
}

function ExceptionsPanel({ extension_uuid, list, qc, toast }: any) {
  // 3CX shape: 4 columns + [+][↑][↓] toolbar. Click [+] adds a blank row that
  // becomes editable inline; up/down reorder priority. Each row binds to:
  //   • Calls from Caller ID  (E.164 / pattern / "anonymous")
  //   • Received during        (schedule: All Hours / Office Hours / Out of Office Hours / specific)
  //   • Forward to             (destination type+target picker)
  //   • Rebound                (toggle — if exception target doesn't pick up, ring user normally)
  const [editing, setEditing] = useState<any | null>(null);

  const sorted = [...(list || [])].sort((a, b) => (a.priority || 0) - (b.priority || 0));

  const save = useMutation({
    mutationFn: (row: any) => fJson(`/api/users-extended/${extension_uuid}/exceptions${row.exception_uuid ? '/' + row.exception_uuid : ''}`, {
      method: row.exception_uuid ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(row),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['forwarding-exceptions', extension_uuid] }); setEditing(null); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });
  const del = useMutation({
    mutationFn: (uuid: string) => fJson(`/api/users-extended/${extension_uuid}/exceptions/${uuid}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forwarding-exceptions', extension_uuid] }),
  });
  const move = useMutation({
    mutationFn: ({ uuid, dir }: { uuid: string; dir: 'up' | 'down' }) =>
      fJson(`/api/users-extended/${extension_uuid}/exceptions/${uuid}/move`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ direction: dir }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forwarding-exceptions', extension_uuid] }),
  });

  function addBlank() {
    setEditing({
      enabled: true,
      condition_type: 'caller_id_equals',
      condition_data: '',
      schedule: 'all_hours',
      destination: { type: 'voicemail' },
      rebound: false,
      priority: (sorted[sorted.length - 1]?.priority || 0) + 10,
    });
  }
  const selectedId = editing?.exception_uuid || null;

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center gap-2 pb-2 border-b border-border">
        <div className="font-medium">Exceptions</div>
        <button onClick={addBlank} title="Add" className="btn-ghost p-1"><Plus size={14} /></button>
        <button
          onClick={() => selectedId && move.mutate({ uuid: selectedId, dir: 'up' })}
          disabled={!selectedId}
          title="Move up" className="btn-ghost p-1 disabled:opacity-40"
        ><ChevronUp size={14} /></button>
        <button
          onClick={() => selectedId && move.mutate({ uuid: selectedId, dir: 'down' })}
          disabled={!selectedId}
          title="Move down" className="btn-ghost p-1 disabled:opacity-40"
        ><ChevronDown size={14} /></button>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Calls from Caller ID</th>
            <th>Received during</th>
            <th>Forward to</th>
            <th className="text-right">Rebound</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((e: any) => (
            <tr key={e.exception_uuid}
                onClick={() => setEditing(e)}
                className={`cursor-pointer ${selectedId === e.exception_uuid ? 'bg-primary/10' : 'hover:bg-elevated'}`}>
              <td className="font-mono text-xs">{e.condition_data || '(any)'}</td>
              <td className="text-xs">{scheduleLabel(e.schedule)}</td>
              <td className="text-xs">{destinationLabel(e.destination)}</td>
              <td className="text-right text-xs">{e.rebound ? '✓' : ''}</td>
              <td className="text-right">
                <button className="btn-ghost p-1 text-danger" onClick={(ev) => { ev.stopPropagation(); del.mutate(e.exception_uuid); }}>
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr><td colSpan={5} className="text-center text-textSecondary py-6 text-sm">No exceptions yet — click <b>+</b> to add one.</td></tr>
          )}
        </tbody>
      </table>

      {editing && <ExceptionRowEditor row={editing} onChange={setEditing} onSave={() => save.mutate(editing)} onCancel={() => setEditing(null)} saving={save.isPending} />}
    </div>
  );
}

function scheduleLabel(s: string): string {
  switch (s) {
    case 'all_hours':        return 'All Hours';
    case 'office_hours':     return 'During Office Hours';
    case 'out_office_hours': return 'Out of Office Hours';
    default:                 return s || 'All Hours';
  }
}
function destinationLabel(d: any): string {
  if (!d?.type) return '—';
  const t = FALLBACK_DEST_TYPES.find(x => x.v === d.type)?.label || d.type;
  return d.target ? `${t} → ${d.target}` : t;
}

function ExceptionRowEditor({ row, onChange, onSave, onCancel, saving }: any) {
  const set = (k: string, v: any) => onChange({ ...row, [k]: v });
  return (
    <div className="border border-primary/40 rounded-md p-3 space-y-3 bg-elevated/40">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-textSecondary mb-1">Calls from Caller ID</label>
          <input className="input font-mono" value={row.condition_data || ''} onChange={e => set('condition_data', e.target.value)} placeholder="+15555551234, anonymous, or pattern" />
        </div>
        <div>
          <label className="block text-xs text-textSecondary mb-1">Received during</label>
          <select className="input" value={row.schedule || 'all_hours'} onChange={e => set('schedule', e.target.value)}>
            <option value="all_hours">All Hours</option>
            <option value="office_hours">During Office Hours</option>
            <option value="out_office_hours">Out of Office Hours</option>
          </select>
        </div>
      </div>
      <DestinationField label="Forward to" value={row.destination} onChange={v => set('destination', v)} />
      <label className="text-sm flex items-center gap-2">
        <input type="checkbox" checked={!!row.rebound} onChange={e => set('rebound', e.target.checked)} />
        <span>Rebound (if forward target doesn't answer, ring me normally)</span>
      </label>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn">Cancel</button>
        <button onClick={onSave} disabled={saving} className="btn-primary inline-flex items-center gap-1">
          <Save size={14} /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// Boss-Secretary panel — per-user config (not the standalone profile-list page).
// Matches 3CX layout: Allow toggle, Secretary picker, Additional Call Handlers,
// Active During schedule, Call Types filter, Allowed Direct Callers, Mode ON/OFF.
function BossSecretaryPanel({ extension_uuid, qc, toast }: any) {
  const cfg = useQuery<any>({
    queryKey: ['boss-secretary-cfg', extension_uuid],
    queryFn: () => fJson(`/api/users-extended/${extension_uuid}/boss-secretary`).catch(() => ({})),
  });
  const [form, setForm] = useState<any>({});
  useEffect(() => { if (cfg.data) setForm(cfg.data); }, [cfg.data]);
  const f = { ...(cfg.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/boss-secretary`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(f),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['boss-secretary-cfg', extension_uuid] }); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  return (
    <div className="card p-4 space-y-4">
      <label className="text-sm flex items-start gap-2">
        <input type="checkbox" checked={!!f.allow} onChange={e => set('allow', e.target.checked)} className="mt-0.5" />
        <div>
          <div className="font-medium">Allow Boss-Secretary</div>
          <div className="text-xs text-textSecondary">Enables Boss-Secretary configuration and exposes controls in the apps.</div>
        </div>
      </label>

      <Field label="Assign a Secretary/PA">
        <input className="input" value={f.secretary || ''} onChange={e => set('secretary', e.target.value)} placeholder="Search or enter number..." />
        <div className="text-xs text-textSecondary mt-1">This person receives your incoming calls first and screens them on your behalf.</div>
      </Field>

      <Field label="Additional Call Handlers">
        <input className="input" value={f.additional_handlers || ''} onChange={e => set('additional_handlers', e.target.value)} placeholder="1000,1003-1005" />
        <div className="text-xs text-textSecondary mt-1">Other users who can also screen or connect calls to you (e.g. 1000,1003-1005).</div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Active During">
          <select className="input" value={f.active_during || 'all_hours'} onChange={e => set('active_during', e.target.value)}>
            <option value="all_hours">All Hours</option>
            <option value="office_hours">Office Hours</option>
            <option value="out_office_hours">Out of Office Hours</option>
          </select>
        </Field>
        <Field label="Call Types">
          <select className="input" value={f.call_types || 'all'} onChange={e => set('call_types', e.target.value)}>
            <option value="all">All Calls</option>
            <option value="external">External Calls Only</option>
            <option value="internal">Internal Calls Only</option>
          </select>
        </Field>
      </div>

      <Field label="Allowed Direct Callers">
        <input className="input" value={f.allowed_direct || ''} onChange={e => set('allowed_direct', e.target.value)} placeholder="+123456789, 1000, 1003-1005" />
        <div className="text-xs text-textSecondary mt-1">Trusted callers who can reach you directly (executives, colleagues, or external numbers) e.g. +123456789, 1000,1003-1005.</div>
      </Field>

      <Field label="Boss-Secretary Mode">
        <div className="flex items-center gap-4">
          <label className="text-sm flex items-center gap-1"><input type="radio" checked={f.mode === 'off'} onChange={() => set('mode', 'off')} /> OFF</label>
          <label className="text-sm flex items-center gap-1"><input type="radio" checked={f.mode !== 'off'} onChange={() => set('mode', 'on')} /> ON</label>
        </div>
        <div className="text-xs text-textSecondary mt-1">Controls whether Boss-Secretary routing is currently active.</div>
      </Field>

      <div className="flex justify-end">
        <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary inline-flex items-center gap-1">
          <Save size={14} /> {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function GreetingPicker({ extension_uuid, profile_name, audio_id, onChange, toast }: any) {
  const recordOnPhone = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/record-greeting`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile_name }),
    }),
    onSuccess: () => toast('success', `Calling your phone — record after the beep, then hang up. Greeting saves as greeting_${profile_name}.wav.`),
    onError: (e: any) => toast('error', e.message),
  });
  return (
    <AudioPicker value={audio_id || ''} onChange={v => onChange(v || null)} placeholder="No greeting (uses default)" />
  );
}

function DestinationField({ label, value, onChange, sameAs }: { label: string; value: any; onChange: (v: any) => void; sameAs?: string }) {
  const v = value || {};
  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{label}</label>
      <div className="flex gap-2">
        <select className="input" value={v.type || ''} onChange={e => onChange({ ...v, type: e.target.value || undefined })}>
          {sameAs && <option value="">Same as {sameAs}</option>}
          {!sameAs && <option value="">— Choose —</option>}
          {FALLBACK_DEST_TYPES.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
        </select>
        {v.type && v.type !== 'voicemail' && v.type !== 'busy' && v.type !== 'end_call' && (
          <input className="input flex-1" value={v.target || ''} onChange={e => onChange({ ...v, target: e.target.value })} placeholder="extension / number / uuid" />
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: any) {
  return (
    <div>
      <div className="text-sm font-medium border-b border-border pb-1 mb-3">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children }: any) {
  return <div><label className="block text-sm text-textSecondary mb-1">{label}</label>{children}</div>;
}
function Toggle({ label, v, on }: { label: string; v: boolean; on: (v: boolean) => void }) {
  return <label className="text-sm flex items-center gap-2 py-1"><input type="checkbox" checked={v} onChange={e => on(e.target.checked)} /> {label}</label>;
}
